import clsx from "clsx";
import { useState } from "react";

import type { JobStatus } from "@/lib/types";

import { useStore } from "@/lib/store";

interface FilterButtonProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

function FilterButton({ label, active, onClick }: FilterButtonProps): JSX.Element {
  return (
    <button
      onClick={onClick}
      className={clsx(
        "rounded px-2 py-0.5 text-[10px] transition-all",
        active
          ? "border border-cyber-cyan/50 bg-cyber-cyan/20 text-cyber-cyan"
          : "border border-transparent text-cyber-muted hover:border-cyber-border hover:text-cyber-text",
      )}
    >
      {label}
    </button>
  );
}

interface TabsProps {
  value: "all" | "mine";
  allCount?: number;
  onChange: (value: "all" | "mine") => void;
}

function MineTabs({ value, allCount, onChange }: TabsProps): JSX.Element {
  return (
    <div className="inline-flex items-center rounded-full bg-cyber-bg p-0.5">
      <button
        type="button"
        onClick={() => onChange("mine")}
        className={clsx(
          "rounded px-2 py-0.5 text-xs transition-all",
          value === "mine"
            ? "bg-cyber-cyan/20 text-cyber-cyan"
            : "text-cyber-muted hover:text-cyber-text",
        )}
      >
        My Chats
      </button>
      <button
        type="button"
        onClick={() => onChange("all")}
        className={clsx(
          "rounded px-2 py-0.5 text-xs transition-all",
          value === "all"
            ? "bg-cyber-cyan/20 text-cyber-cyan"
            : "text-cyber-muted hover:text-cyber-text",
        )}
      >
        <span className="inline-flex items-center gap-1">
          All
          <span className="rounded-full bg-cyber-surface/70 px-1.5 py-0.5 text-[10px] leading-none text-cyber-muted">
            {typeof allCount === "number" ? allCount : "…"}
          </span>
        </span>
      </button>
    </div>
  );
}

const STATUS_OPTIONS: { label: string; value: JobStatus | undefined }[] = [
  { label: "All", value: undefined },
  { label: "Pending", value: "pending" },
  { label: "Processing", value: "processing" },
  { label: "Completed", value: "completed" },
  { label: "Cancelled", value: "cancelled" },
  { label: "Failed", value: "failed" },
];

const SOURCE_OPTIONS: { label: string; value: string | undefined }[] = [
  { label: "All", value: undefined },
  { label: "Web", value: "web" },
  { label: "Slack", value: "slack" },
  { label: "Linear", value: "linear" },
];

interface SidebarFiltersProps {
  allCount?: number;
}

export function SidebarFilters({ allCount }: SidebarFiltersProps): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const filters = useStore((state) => state.filters);
  const setFilter = useStore((state) => state.setFilter);
  const clearFilters = useStore((state) => state.clearFilters);

  const hasFilters = filters.status || filters.source || filters.search || filters.includeAutomated;

  return (
    <div className="border-b border-cyber-border px-3 py-2">
      {/* Quick filters row */}
      <div className="flex items-center gap-1">
        <MineTabs
          value={filters.mine ? "mine" : "all"}
          allCount={allCount}
          onChange={(value) => setFilter("mine", value === "mine" ? true : undefined)}
        />
        <div className="flex-1" />
        <button
          onClick={() => setExpanded(!expanded)}
          className={clsx(
            "px-2 py-0.5 text-xs text-cyber-muted transition-colors hover:text-cyber-text",
            expanded && "text-cyber-cyan",
          )}
        >
          {expanded ? "▼ Less" : "▶ More"}
        </button>
        {hasFilters && (
          <button
            onClick={clearFilters}
            className="ml-auto text-[10px] text-cyber-red hover:text-cyber-red/80"
          >
            Clear
          </button>
        )}
      </div>

      {/* Expanded filters */}
      {expanded && (
        <div className="mt-2 space-y-2 border-t border-cyber-border pt-2">
          {/* Status filters */}
          <div>
            <div className="mb-1 text-[10px] uppercase tracking-wider text-cyber-muted">Status</div>
            <div className="flex flex-wrap gap-1">
              {STATUS_OPTIONS.map((opt) => (
                <FilterButton
                  key={opt.label}
                  label={opt.label}
                  active={filters.status === opt.value}
                  onClick={() => setFilter("status", opt.value)}
                />
              ))}
            </div>
          </div>

          {/* Source filters */}
          <div>
            <div className="mb-1 text-[10px] uppercase tracking-wider text-cyber-muted">
              Activity
            </div>
            <FilterButton
              label="Include automated runs"
              active={Boolean(filters.includeAutomated)}
              onClick={() => setFilter("includeAutomated", !filters.includeAutomated)}
            />
          </div>

          {/* Source filters */}
          <div>
            <div className="mb-1 text-[10px] uppercase tracking-wider text-cyber-muted">Source</div>
            <div className="flex flex-wrap gap-1">
              {SOURCE_OPTIONS.map((opt) => (
                <FilterButton
                  key={opt.label}
                  label={opt.label}
                  active={filters.source === opt.value}
                  onClick={() => setFilter("source", opt.value)}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
